import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { classService, getErrorCode, getErrorMessage } from '../services/api';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { Spinner } from '../components/ui';
import { siteUrl } from '../utils/site';
import { useToast } from '../components/Toast';
import {
  clearPendingInviteCode,
  getPendingInviteCode,
  normalizeInviteCode,
  setPendingInviteCode,
} from '../utils/invite';

// Only same-app paths: an absolute or protocol-relative URL in ?next= would make the login form an open redirect.
function safeNextPath(next: string | null): string {
  if (next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\')) {
    return next;
  }
  return '/dashboard';
}

const Login: React.FC = () => {
  const location = useLocation();
  // From the confirmation page (ACC-16): the address is filled in.
  const navigationState = location.state as { message?: string; email?: string } | null;
  const [email, setEmail] = useState(navigationState?.email || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading, login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryInviteCode = normalizeInviteCode(searchParams.get('invite'));
  const pendingInviteCode = getPendingInviteCode();
  const inviteCode = useMemo(() => queryInviteCode || pendingInviteCode, [pendingInviteCode, queryInviteCode]);
  const navigationMessage = navigationState?.message;
  const nextPath = safeNextPath(searchParams.get('next'));

  useEffect(() => {
    if (queryInviteCode) {
      setPendingInviteCode(queryInviteCode);
    }
  }, [queryInviteCode]);

  // Already logged in (e.g. a bookmarked /login). Not while the form is submitting: handleSubmit still has the
  // invite to process and navigates itself.
  if (user && !authLoading && !loading) {
    return <Navigate to={nextPath} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loggedInUser = await login(email, password);

      if (inviteCode && loggedInUser.role === 'student') {
        try {
          await classService.joinClassByInviteCode(inviteCode);
        } catch (joinError) {
          toast.error(`Logged in, but joining the class failed: ${getErrorMessage(joinError, 'Unable to join class from invite link.')}`);
        } finally {
          clearPendingInviteCode();
        }
      } else if (inviteCode) {
        clearPendingInviteCode();
      }

      navigate(inviteCode ? '/dashboard' : nextPath, { replace: true });
    } catch (err) {
      // ACC-16: the right password, but the address isn't confirmed yet: say so and offer a new link.
      if (getErrorCode(err) === 'email_not_confirmed') {
        navigate('/check-email', { state: { email: email.trim(), fromLogin: true } });
        return;
      }
      setError(getErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Login</h2>
      <p className="mt-1 mb-6 text-sm text-gray-500 dark:text-gray-400">Welcome back. Log in to continue to your lessons.</p>

      <div className="space-y-3 mb-5 empty:hidden">
        {error && (
          <div className="alert alert-error" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        {navigationMessage && (
          <div className="alert alert-success" role="status">
            <CheckCircle2 />
            <span>{navigationMessage}</span>
          </div>
        )}

        {inviteCode && (
          <div className="alert alert-info">
            <Info />
            <span>You are joining a class via invite link. Log in to be enrolled automatically.</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="label">Email</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            autoComplete="email"
            required
          />
        </div>

        <div>
          <label htmlFor="login-password" className="label">Password</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            autoComplete="current-password"
            required
          />
        </div>

        <button type="submit" disabled={loading} className="btn btn-primary btn-lg w-full !mt-6">
          {loading && <Spinner className="w-4 h-4 text-white" />}
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Don't have an account?{' '}
        <Link to={inviteCode ? `/register?invite=${inviteCode}` : '/register'} className="link">
          Register
        </Link>
      </p>
      <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-500">
        <a href={siteUrl('/privacy')} target="_blank" rel="noopener noreferrer" className="hover:underline">
          Privacy Policy
        </a>
      </p>
    </AuthShell>
  );
};

export default Login;
